import { TranslatePipe, TranslateService } from "@ngx-translate/core";

import { ChangeDetectionStrategy, Component, computed, inject, input, model, signal } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";

import { L2vpnSubscriptionDetails } from "../../../pages/subscription-detail/types";
import { AuthService } from "../../../services/authservice";
import { MessageService } from "../../../services/messageservice";
import { DialogSelfServiceComponent } from "../../dialogs/dialog-selfservice/dialog-selfservice";
import { ModifyL2vpnFwButton } from "../tabbed-detail-container/components/modify-l2vpn-fw-button/modify-l2vpn-fw-button";
import { openSelfserviceDialog } from "../tabbed-detail-container/open-selfservice-dialog";
import { TabbedDetailBase } from "../tabbed-detail-container/tabbed-detail-base";
import { descriptionForEntity } from "../tabbed-detail-container/tabbed-detail-container.helpers";
import { SelfserviceState } from "../tabbed-detail-container/tabbed-detail-container.types";
import { L2vpnEsiPanel } from "./components/l2vpn-esi-panel/l2vpn-esi-panel";
import { L2vpnFirewallInterconnectPanel } from "./components/l2vpn-firewall-interconnect-panel/l2vpn-firewall-interconnect-panel";

@Component({
  selector: "l2vpn-detail-container",
  templateUrl: "l2vpn-detail-container.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [L2vpnEsiPanel, L2vpnFirewallInterconnectPanel, ModifyL2vpnFwButton, TabbedDetailBase, TranslatePipe],
})
export class L2vpnDetailContainer {
  private translate = inject(TranslateService);
  private auth = inject(AuthService);
  private dialog = inject(MatDialog);
  private msgsrv = inject(MessageService);

  subscription = input.required<L2vpnSubscriptionDetails>();
  canModify = input<boolean>(false);

  protected activeTabIndex = model(0);

  protected firewallEnabled = computed(() => this.subscription().firewallEnabled ?? false);
  protected isInfrastructureDelegate = computed(
    () =>
      this.auth.isCurrentOrganisation(this.subscription().customerId) && this.auth.hasRole(["Infraverantwoordelijke"]),
  );
  protected showModifyButton = computed(
    () => this.canModify() && this.auth.isSelfServiceEnabled && !this.firewallEnabled(),
  );

  protected navTabs = computed(() => {
    const s = this.subscription();
    const fwTabs = s.firewallInterconnects.map(() => ({
      title: this.translate.instant("ServiceElement.FirewallInterconnect"),
      subtitle: "-",
    }));
    const esiTabs = s.esis.map((esi) => {
      const endpoints = esi.endpoints ?? [];
      if (!endpoints.length) {
        return { title: "-", subtitle: "-" };
      }
      return {
        title:
          endpoints.length > 1 ?
            this.translate.instant("ServiceElement.MultipleEndpoints")
          : descriptionForEntity(endpoints[0].port),
        subtitle: `VLAN ${endpoints[0].vlanrange}`,
      };
    });
    return [...fwTabs, ...esiTabs];
  });

  protected activeFirewallInterconnect = computed(() => {
    const idx = this.activeTabIndex();
    const fw = this.subscription().firewallInterconnects;
    return idx < fw.length ? fw[idx] : null;
  });

  protected activeEsi = computed(() => {
    const s = this.subscription();
    const idx = this.activeTabIndex() - s.firewallInterconnects.length;
    return idx >= 0 && idx < s.esis.length ? s.esis[idx] : null;
  });

  private selfserviceState = signal<SelfserviceState | null>(null);
  private dialogRef: MatDialogRef<DialogSelfServiceComponent<L2vpnSubscriptionDetails>> | undefined;

  protected openSelfserviceDialog(type: string) {
    const activeEsi = this.activeEsi();
    if (!activeEsi) return;

    const ref = openSelfserviceDialog(
      type,
      this.dialog,
      this.auth,
      this.subscription(),
      activeEsi,
      this.activeTabIndex(),
      this.selfserviceState,
      this.dialogRef,
    );
    if (ref) {
      this.dialogRef = ref as MatDialogRef<DialogSelfServiceComponent<L2vpnSubscriptionDetails>>;
      this.dialogRef.afterClosed().subscribe((event: string) => {
        this.msgsrv.setSelfServiceMessage(event);
      });
    }
  }
}
